import * as THREE from 'three/webgpu';

import { createRenderer, refreshCaps, logBackendOnce } from './renderer.js';
import { createLoop } from './loop.js';
import { createPerfGovernor } from './perfGovernor.js';
import { createBus } from './events.js';
import { Registry } from './registry.js';
import { uTime } from './uniforms.js';

/**
 * @file The engine shell: renderer + scene + camera + fixed-step loop + bus,
 * and the level lifecycle. Deliberately small — everything else (day/night,
 * post, player, enemies) attaches to the extension points below.
 *
 * Extension points later waves use, all optional and all safe to leave unset:
 *   `engine.post`      {@link import('../types.js').PostStage} — if set, its
 *                      `render()` replaces `renderer.render(scene, camera)`.
 *   `engine.dayNight`  object with `update(dt)`; called on the fixed step.
 *   `engine.weather`   {@link import('../systems/Weather.js').Weather}, set by
 *                      a level's `createPost`. NOT ticked here — it rides an
 *                      `engine.systems` entry, because it also has to move the
 *                      rain column in the RENDER phase. The field exists so the
 *                      HUD and the console can reach it.
 *   `engine.systems`   array of `{update?(dt), render?(dt, alpha), dispose?()}`.
 *   `engine.level.update(dt, elapsed)` — optional per-level animation hook.
 *
 * PERFORMANCE. With `autoFrameCap` (default) the Engine measures wall-clock
 * frame intervals and lets `core/perfGovernor.js` decide whether to cap the
 * RENDER rate to `frameCapHz` — a locked 30 beats a jittery 40 on an
 * integrated GPU. Only `onRender` is paced: `onFixed` keeps its 60Hz, so
 * simulation rate and determinism are identical capped or not. The cap is
 * announced once on `'perf:framecap'` ({hz}, 0 = released) and re-evaluated per
 * level. `setFrameCap(hz)` overrides it by hand and retires the governor.
 *
 * React usage (StrictMode-safe): construct, `await init()`, and on unmount call
 * `dispose()`. `dispose()` is idempotent and is safe to call while `init()` is
 * still in flight — the in-flight init tears itself down.
 *
 * IMPORTANT — pass a CONTAINER, not a canvas, from React.
 * three's WebGL2 backend calls `WEBGL_lose_context.loseContext()` in
 * `dispose()`, which permanently kills that canvas: under StrictMode's
 * mount -> unmount -> mount, a second Engine bound to the SAME <canvas> gets a
 * dead context and renders a blank screen (WebGPU survives this; WebGL2 does
 * not — and `?forceWebGL=1` is a required smoke test). So when the first
 * argument is anything other than a <canvas>, the Engine creates and OWNS a
 * canvas inside it and removes it on dispose, guaranteeing every Engine
 * instance gets a pristine context.
 */

/** @typedef {import('../types.js').EngineOpts} EngineOpts */
/** @typedef {import('../types.js').Level} Level */
/** @typedef {import('../types.js').LevelBuildCtx} LevelBuildCtx */

const DEG = Math.PI / 180;

/** @type {Required<Pick<EngineOpts,'antialias'|'alpha'|'forceWebGL'|'fov'|'pitchDeg'|'yawDeg'|'distance'|'near'|'far'|'fixedStep'|'maxSubSteps'|'autoResize'|'autoStart'|'autoFrameCap'|'frameCapHz'>>} */
const DEFAULTS = {
  antialias: true,
  alpha: false,
  forceWebGL: false,
  // Low FOV + steep pitch is the signature look: near-isometric, minimal
  // perspective distortion, roofs still readable.
  fov: 30,
  pitchDeg: 52,
  yawDeg: 45,
  distance: 26,
  near: 1,
  far: 600,
  fixedStep: 1 / 60,
  maxSubSteps: 5,
  autoResize: true,
  autoStart: false,
  // Integrated GPUs land around 40fps with constant jitter, which reads worse
  // than a locked 30. The governor watches real frame intervals and caps the
  // RENDER rate only — the 60Hz fixed step is never touched.
  autoFrameCap: true,
  frameCapHz: 30,
};

export class Engine {
  /**
   * @param {HTMLCanvasElement|HTMLElement} host A `<canvas>` to draw into, or
   *   any container element — in which case the Engine creates and owns a
   *   canvas inside it (see the WebGL context-loss note above).
   * @param {EngineOpts} [opts]
   */
  constructor(host, opts = {}) {
    if (!host) throw new Error('Engine: a canvas or container element is required');

    const ownsCanvas = !(typeof HTMLCanvasElement !== 'undefined' && host instanceof HTMLCanvasElement);
    /** @type {HTMLCanvasElement} */
    this.canvas = ownsCanvas ? createCanvasIn(/** @type {HTMLElement} */ (host)) : /** @type {HTMLCanvasElement} */ (host);
    /** @type {boolean} True when `dispose()` should remove the canvas too. */
    this._ownsCanvas = ownsCanvas;

    /** @type {EngineOpts & typeof DEFAULTS} */
    this.opts = { ...DEFAULTS, ...opts };

    const { renderer, caps } = createRenderer(this.canvas, {
      antialias: this.opts.antialias,
      alpha: this.opts.alpha,
      forceWebGL: this.opts.forceWebGL,
      pixelRatio: this.opts.pixelRatio,
    });

    /** @type {THREE.WebGPURenderer} */
    this.renderer = renderer;
    /** @type {import('../types.js').EngineCaps} Mutated in place by init(). */
    this.caps = caps;

    // three's WebGL backend calls `loseContext()` inside its own `dispose()`,
    // and Chrome dispatches `webglcontextlost` synchronously — before three
    // removes its listener. Result: every teardown logs a bogus "Device Lost"
    // error. Route the hook through the bus and swallow it once we're disposing;
    // a REAL device loss while running still reports.
    renderer.onDeviceLost = (info) => {
      if (this._disposed) return;
      console.error(`[engine] ${info?.api ?? 'GPU'} device lost: ${info?.message ?? 'unknown'}`);
      this.bus.emit('device:lost', info);
    };

    /** @type {THREE.Scene} */
    this.scene = new THREE.Scene();
    // Placeholder clear colour matching the app chrome; T2's sky replaces it.
    this.scene.background = new THREE.Color(0x0a0a0f);

    /** @type {THREE.PerspectiveCamera} */
    this.camera = new THREE.PerspectiveCamera(
      this.opts.fov,
      this._aspect(),
      this.opts.near,
      this.opts.far,
    );

    /** @type {import('../types.js').EventBus} */
    this.bus = createBus();

    /** Per-LEVEL resource owner. Replaced (and the old one disposed) on every
     *  `setLevel`. Do not cache this across level switches. */
    this.registry = new Registry('boot');

    /** @type {Level|null} */
    this.level = null;

    /** @type {import('../types.js').PostStage|null} Set by T2. */
    this.post = null;

    /** @type {{update?: (dt:number)=>void, dispose?: ()=>void}|null} Set by T2. */
    this.dayNight = null;

    /** @type {any} The weather brain, set by a level's `createPost`. Ticked by
     *  its own `engine.systems` entry, which also owns its disposal. */
    this.weather = null;

    /** @type {Array<{update?: (dt:number)=>void, render?: (dt:number, alpha:number)=>void, dispose?: ()=>void}>} */
    this.systems = [];

    // Camera orbit state. T5's CameraRig drives `target` and calls
    // `updateCamera()`; until then the defaults frame the origin.
    /** @type {THREE.Vector3} */
    this.target = new THREE.Vector3(0, 0, 0);
    this.pitch = this.opts.pitchDeg * DEG;
    this.yaw = this.opts.yawDeg * DEG;
    this.distance = this.opts.distance;

    this._initialized = false;
    this._initPromise = null;
    this._disposed = false;
    this._levelToken = 0;
    /** @type {ResizeObserver|null} */
    this._ro = null;
    this._onWindowResize = null;

    /** @type {import('../types.js').Loop} */
    this.loop = createLoop({
      renderer: this.renderer,
      fixedStep: this.opts.fixedStep,
      maxSubSteps: this.opts.maxSubSteps,
      onFixed: (dt) => this._fixed(dt),
      onRender: (dt, alpha) => this._render(dt, alpha),
    });

    /** @type {ReturnType<typeof createPerfGovernor>|null} Null once a manual
     *  `setFrameCap()` has taken over, or when `autoFrameCap` is off. */
    this._gov = this.opts.autoFrameCap ? createPerfGovernor({ capHz: this.opts.frameCapHz }) : null;
    /** Wall-clock timestamp of the previous `_render`; 0 = none yet. */
    this._lastRenderMs = 0;

    this.updateCamera();
  }

  // ── lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Boots the renderer (async: WebGPU device creation / WebGL2 fallback),
   * resolves `caps`, wires resize. Safe to call twice — returns the same
   * promise.
   * @returns {Promise<this>}
   */
  async init() {
    if (this._initPromise) return this._initPromise;
    this._initPromise = (async () => {
      await this.renderer.init();

      // Torn down while the device was coming up (StrictMode double mount).
      if (this._disposed) {
        this._hardDisposeRenderer();
        return this;
      }

      refreshCaps(this.renderer, this.caps);
      logBackendOnce(this.caps);

      this.resize();
      if (this.opts.autoResize) this._attachResize();

      this._initialized = true;
      this.bus.emit('ready', { caps: this.caps });
      if (this.opts.autoStart) this.start();
      return this;
    })();
    return this._initPromise;
  }

  /** Starts the fixed-step loop. No-op before `init()` resolves or after dispose. */
  start() {
    if (this._disposed || !this._initialized) return;
    this.loop.start();
  }

  /** Stops the loop; state is preserved, `start()` resumes. */
  stop() {
    this.loop.stop();
  }

  /**
   * Releases everything: level, registry, post, systems, renderer, listeners.
   * IDEMPOTENT — required for React StrictMode's mount/unmount/mount.
   */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;

    try {
      this.loop.stop();
    } catch {
      /* renderer may already be gone */
    }

    this._detachResize();

    for (const sys of this.systems) {
      try {
        sys?.dispose?.();
      } catch (err) {
        console.warn('[engine] system dispose failed', err);
      }
    }
    this.systems.length = 0;

    try {
      this.dayNight?.dispose?.();
    } catch {
      /* noop */
    }
    this.dayNight = null;

    try {
      this.post?.dispose?.();
    } catch {
      /* noop */
    }
    this.post = null;

    this._disposeLevel();
    this.registry.disposeAll();

    this.scene.clear();

    // Only tear the renderer down once the device exists; otherwise init()
    // finishes the job when its promise settles.
    if (this._initialized || !this._initPromise) this._hardDisposeRenderer();

    // A canvas we created is dead once the backend disposed it (WebGL2 loses
    // its context) — drop it so the next Engine starts from a clean element.
    if (this._ownsCanvas) {
      try {
        this.canvas.remove();
      } catch {
        /* noop */
      }
    }

    this.bus.emit('disposed');
    this.bus.clear();
  }

  // ── levels ─────────────────────────────────────────────────────────────────

  /**
   * Builds and installs a level. Disposes whatever was loaded before. Stale
   * loads (a second `setLevel` before the first resolves) are discarded and
   * their result disposed.
   *
   * @param {(ctx: LevelBuildCtx) => (Level | Promise<Level>)} loaderFn
   * @param {{seed?: string|number, size?: number, [k: string]: any}} [params]
   * @returns {Promise<Level|null>} `null` if the load was superseded/aborted.
   */
  async setLevel(loaderFn, params = {}) {
    if (this._disposed) return null;
    if (typeof loaderFn !== 'function') throw new Error('Engine.setLevel: loaderFn must be a function');

    if (!this._initialized) await this.init();
    if (this._disposed) return null;

    const token = ++this._levelToken;
    const label = params.id ?? params.levelId ?? 'level';

    this._disposeLevel();
    this.registry.disposeAll();
    this.registry = new Registry(String(label));

    const onProgress = (t, msg) => {
      if (token !== this._levelToken) return;
      this.bus.emit('level:progress', { t, label: msg });
    };

    this.bus.emit('level:loading', { params });

    /** @type {LevelBuildCtx} */
    const ctx = {
      seed: params.seed ?? this.opts.seed ?? 'default',
      size: params.size ?? 256,
      renderer: this.renderer,
      registry: this.registry,
      onProgress,
      caps: this.caps,
      bus: this.bus,
      params,
    };

    try {
      const level = await loaderFn(ctx);

      // Superseded or disposed while building — throw the result away cleanly.
      if (token !== this._levelToken || this._disposed) {
        level?.dispose?.();
        return null;
      }
      if (!level || !level.root) throw new Error('level builder returned no `root`');

      this.level = level;
      this.scene.add(level.root);

      // Re-measure per level. A level swap is already a hitch — a hundred
      // frames of build stall, a fresh scene, a cold pipeline cache — so it is
      // the one honest moment to release the cap and let the governor warm up
      // again on the new level's real cost. Probing mid-play would mean
      // un-capping into a visible stutter every time.
      this._gov?.reset();
      if (this._gov) this._applyFrameCap(0, 'auto');
      this._lastRenderMs = 0;

      this.bus.emit('level:ready', { level, meta: level.meta });
      return level;
    } catch (err) {
      if (token === this._levelToken) {
        console.error('[engine] level build failed', err);
        this.bus.emit('level:error', { error: err });
      }
      throw err;
    }
  }

  /** Removes + disposes the current level. Safe when there is none. */
  _disposeLevel() {
    const level = this.level;
    this.level = null;
    if (!level) return;
    try {
      if (level.root?.parent) level.root.parent.remove(level.root);
      level.dispose?.();
    } catch (err) {
      console.warn('[engine] level dispose failed', err);
    }
  }

  // ── camera ─────────────────────────────────────────────────────────────────

  /**
   * Places the camera on its orbit around `target` from the current
   * pitch/yaw/distance. Call after mutating any of them.
   */
  updateCamera() {
    const cp = Math.cos(this.pitch);
    const sp = Math.sin(this.pitch);
    this.camera.position.set(
      this.target.x + Math.sin(this.yaw) * cp * this.distance,
      this.target.y + sp * this.distance,
      this.target.z + Math.cos(this.yaw) * cp * this.distance,
    );
    this.camera.lookAt(this.target);
    this.camera.updateMatrixWorld();
  }

  /**
   * @param {{pitchDeg?: number, yawDeg?: number, distance?: number, target?: {x:number,y:number,z:number}}} o
   */
  setCameraOrbit(o = {}) {
    if (o.pitchDeg !== undefined) this.pitch = o.pitchDeg * DEG;
    if (o.yawDeg !== undefined) this.yaw = o.yawDeg * DEG;
    if (o.distance !== undefined) this.distance = o.distance;
    if (o.target) this.target.set(o.target.x, o.target.y, o.target.z);
    this.updateCamera();
  }

  // ── sizing ─────────────────────────────────────────────────────────────────

  _aspect() {
    const { w, h } = this._canvasSize();
    return w / h;
  }

  _canvasSize() {
    const el = this.canvas;
    const w = Math.max(1, el.clientWidth || el.width || 1);
    const h = Math.max(1, el.clientHeight || el.height || 1);
    return { w, h };
  }

  /** Matches drawing buffer + camera to the canvas's CSS size. */
  resize() {
    if (this._disposed) return;
    const { w, h } = this._canvasSize();
    // `false`: never write inline style onto the canvas — CSS owns layout.
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.bus.emit('resize', { width: w, height: h });
  }

  _attachResize() {
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.resize());
      this._ro.observe(this.canvas);
    } else if (typeof window !== 'undefined') {
      this._onWindowResize = () => this.resize();
      window.addEventListener('resize', this._onWindowResize);
    }
  }

  _detachResize() {
    this._ro?.disconnect();
    this._ro = null;
    if (this._onWindowResize && typeof window !== 'undefined') {
      window.removeEventListener('resize', this._onWindowResize);
      this._onWindowResize = null;
    }
  }

  // ── frame ──────────────────────────────────────────────────────────────────

  /** @param {number} dt Always `opts.fixedStep`. */
  _fixed(dt) {
    if (this._disposed) return;
    this.dayNight?.update?.(dt);
    for (const sys of this.systems) sys.update?.(dt);
    this.level?.update?.(dt, this.loop.elapsed());
    this.bus.emit('fixed', dt);
  }

  /**
   * @param {number} dt Real (time-scaled) frame delta.
   * @param {number} alpha Interpolation factor into the next fixed step.
   */
  _render(dt, alpha) {
    if (this._disposed) return;
    this._sampleFrameRate();
    uTime.value = this.loop.elapsed();
    for (const sys of this.systems) sys.render?.(dt, alpha);
    this.bus.emit('render', { dt, alpha });
    this.render();
  }

  // ── automatic frame cap ────────────────────────────────────────────────────

  /**
   * Feeds one WALL-CLOCK frame interval to the perf governor and applies
   * whatever cap it asks for.
   *
   * The interval is measured here rather than taken from the loop's `dt` on
   * purpose: `dt` is multiplied by `timeScale`, so a pause (timeScale 0) or any
   * slow-mo would feed the governor times that have nothing to do with how fast
   * the GPU actually is.
   */
  _sampleFrameRate() {
    const gov = this._gov;
    if (!gov) return;
    const t = nowMs();
    const prev = this._lastRenderMs;
    this._lastRenderMs = t;
    // First render of the session/level: no previous timestamp to subtract.
    if (prev === 0) return;
    this._applyFrameCap(gov.sample(t - prev), 'auto');
  }

  /**
   * Pushes a cap onto the loop, once. Comparing against the loop's live value is
   * what keeps the log line and the event from firing every frame.
   * @param {number} hz 0 = uncapped.
   * @param {'auto'|'manual'} [source]
   */
  _applyFrameCap(hz, source = 'manual') {
    const next = typeof hz === 'number' && Number.isFinite(hz) && hz > 0 ? hz : 0;
    if (next === this.loop.frameCap()) return;
    this.loop.setFrameCap(next);
    console.log(
      next > 0
        ? `[engine] frame cap ${next}fps (${source}) — simulation stays at 60Hz`
        : `[engine] frame cap released (${source})`,
    );
    this.bus.emit('perf:framecap', { hz: next });
  }

  /**
   * Manual override for the render cap. Also retires the governor for the rest
   * of the session — a deliberate choice must never be second-guessed by a
   * later measurement.
   * @param {number} hz Frames per second; 0 = uncapped.
   */
  setFrameCap(hz) {
    this._gov = null;
    this._applyFrameCap(hz, 'manual');
  }

  /** Draws one frame through the post stage when present. */
  render() {
    if (this._disposed) return;
    if (this.post && typeof this.post.render === 'function') this.post.render();
    else this.renderer.render(this.scene, this.camera);
  }

  _hardDisposeRenderer() {
    try {
      this.renderer.setAnimationLoop(null);
    } catch {
      /* noop */
    }
    try {
      this.renderer.dispose();
    } catch (err) {
      console.warn('[engine] renderer dispose failed', err);
    }
  }
}

/** @returns {number} Wall-clock milliseconds, monotonic where available. */
function nowMs() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * Creates a canvas that fills `host`. Inline styles (not CSS classes) so the
 * engine works in the editor/gallery without importing the app stylesheet.
 * @param {HTMLElement} host
 * @returns {HTMLCanvasElement}
 */
function createCanvasIn(host) {
  const canvas = document.createElement('canvas');
  canvas.style.display = 'block';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.outline = 'none';
  canvas.setAttribute('data-engine-canvas', '');
  host.appendChild(canvas);
  return canvas;
}

/**
 * @param {HTMLCanvasElement|HTMLElement} host
 * @param {EngineOpts} [opts]
 * @returns {Promise<Engine>} Already `init()`ed.
 */
export async function createEngine(host, opts) {
  const engine = new Engine(host, opts);
  await engine.init();
  return engine;
}
